import type { Policy } from '@communities/content-management/domain';
import { useCreateCommunityPolicy, useUpdateCommunityPolicy } from '@communities/content-management/hooks';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useState, useCallback, useMemo } from 'react';

import type { DialogFormData, DialogStep } from '../../types';
import { DialogStep as DialogStepEnum } from '../../types';
import { transformDialogDataToPolicyInput } from '../../utils/dataTransformers';

import type { DialogActions, DialogContextValue } from './DialogContext';
import { DialogContext } from './DialogContext';

/**
 * Helper function to create form data from an existing policy
 */
function createFormDataFromPolicy(policy: Policy): DialogFormData {
  return {
    policyInfo: {
      name: policy.name,
      description: policy.description || '',
      isEnabled: policy.isEnabled,
    },
    textLabels: {
      textLabels: policy.labels
        .filter(label => typeof label.severity === 'number')
        .map(label => ({
          labelId: label.labelId,
          severity: label.severity as number,
        })),
    },
    imageLabels: {
      imageLabels: policy.labels
        .filter(label => typeof label.scoreThreshold === 'number')
        .map(label => ({
          labelId: label.labelId,
          scoreThreshold: label.scoreThreshold as number,
        })),
    },
    review: {
      isValid: true,
      warnings: [],
    },
  };
}

/**
 * Helper function to create empty form data
 */
function createEmptyFormData(): DialogFormData {
  return {
    policyInfo: {
      name: '',
      description: '',
      isEnabled: true,
    },
    textLabels: {
      textLabels: [],
    },
    imageLabels: {
      imageLabels: [],
    },
    review: {
      isValid: false,
      warnings: [],
    },
  };
}

/**
 * Step validation function
 */
function validateStep(step: DialogStep, formData: DialogFormData): boolean {
  switch (step) {
    case DialogStepEnum.POLICY_INFO:
      return formData.policyInfo.name.trim().length > 0;

    case DialogStepEnum.TEXT_LABELS:
      // Text labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.IMAGE_LABELS:
      // Image labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.REVIEW:
      // Only require policy info to be valid - labels are optional
      return validateStep(DialogStepEnum.POLICY_INFO, formData);

    default:
      return false;
  }
}

/**
 * Get next step in dialog flow
 */
function getNextStep(currentStep: DialogStep): DialogStep {
  switch (currentStep) {
    case DialogStepEnum.POLICY_INFO:
      return DialogStepEnum.TEXT_LABELS;
    case DialogStepEnum.TEXT_LABELS:
      return DialogStepEnum.IMAGE_LABELS;
    case DialogStepEnum.IMAGE_LABELS:
      return DialogStepEnum.REVIEW;
    case DialogStepEnum.REVIEW:
      return DialogStepEnum.REVIEW; // Stay on review step
    default:
      return DialogStepEnum.POLICY_INFO;
  }
}

/**
 * Get previous step in dialog flow
 */
function getPreviousStep(currentStep: DialogStep): DialogStep {
  switch (currentStep) {
    case DialogStepEnum.TEXT_LABELS:
      return DialogStepEnum.POLICY_INFO;
    case DialogStepEnum.IMAGE_LABELS:
      return DialogStepEnum.TEXT_LABELS;
    case DialogStepEnum.REVIEW:
      return DialogStepEnum.IMAGE_LABELS;
    case DialogStepEnum.POLICY_INFO:
      return DialogStepEnum.POLICY_INFO; // Stay on first step
    default:
      return DialogStepEnum.POLICY_INFO;
  }
}

/**
 * Step order for proper index-based comparison
 * DialogStep enum values are strings, so we need explicit ordering
 */
const STEP_ORDER: DialogStep[] = [DialogStepEnum.POLICY_INFO, DialogStepEnum.TEXT_LABELS, DialogStepEnum.IMAGE_LABELS, DialogStepEnum.REVIEW];

/**
 * Get the index of a step in the step order
 */
function getStepIndex(step: DialogStep): number {
  return STEP_ORDER.indexOf(step);
}

/**
 * Validate step transition
 */
export function validateStepTransition(fromStep: DialogStep, toStep: DialogStep, formData: DialogFormData): boolean {
  const fromIndex = getStepIndex(fromStep);
  const toIndex = getStepIndex(toStep);

  // Allow moving backwards
  if (toIndex <= fromIndex) {
    return true;
  }

  // When moving forward, validate all steps from the first step up to (but not including) the target step
  // This ensures policy name is always required before accessing any step beyond the first
  for (let i = 0; i < toIndex; i++) {
    if (!validateStep(STEP_ORDER[i], formData)) {
      return false;
    }
  }

  return true;
}

/**
 * DialogProvider props interface
 */
export interface DialogProviderProps {
  children: React.ReactNode;
  initialPolicy?: Policy;
  communityId: string;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * DialogProvider component that manages dialog state and actions
 */
export function DialogProvider({ children, initialPolicy, communityId, onComplete, onCancel }: DialogProviderProps) {
  // Initialize state with useState hooks
  const initialFormData = useMemo(() => (initialPolicy ? createFormDataFromPolicy(initialPolicy) : createEmptyFormData()), [initialPolicy]);

  const [currentStep, setCurrentStep] = useState<DialogStep>(DialogStepEnum.POLICY_INFO);
  const [formData, setFormData] = useState<DialogFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<DomainError[]>([]);

  // Compute isValid based on current step and form data
  const isValid = useMemo(() => validateStep(currentStep, formData), [currentStep, formData]);

  // Policy mutation hooks
  const createHook = useCreateCommunityPolicy(communityId);
  const updateHook = useUpdateCommunityPolicy(communityId);

  const { createPolicy } = createHook;
  const { updatePolicy } = updateHook;

  // Update form data action
  const updateFormData = useCallback((data: Partial<DialogFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
    setErrors([]); // Clear errors when form data changes
  }, []);

  // Navigate to specific step
  const goToStep = useCallback(
    (step: DialogStep) => {
      const isValidTransition = validateStepTransition(currentStep, step, formData);

      if (isValidTransition) {
        setCurrentStep(step);
      }
    },
    [currentStep, formData]
  );

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    const nextStep = getNextStep(currentStep);

    goToStep(nextStep);
  }, [currentStep, goToStep]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousStep(currentStep);

    goToStep(previousStep);
  }, [currentStep, goToStep]);

  // Submit dialog data
  const submitDialog = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const policyInput = transformDialogDataToPolicyInput(formData);

      const success = initialPolicy ? await updatePolicy(communityId, initialPolicy.id, policyInput) : await createPolicy(communityId, policyInput);

      if (success) {
        // Loading state will be reset when component unmounts via onComplete
        onComplete();
      } else {
        // Reset loading state if operation failed
        setIsSubmitting(false);
      }
    } catch (error) {
      const domainErrors = transformErrors(error);

      // Reset loading state on error
      setIsSubmitting(false);
      setErrors(domainErrors);
    }
  }, [formData, initialPolicy, communityId, createPolicy, updatePolicy, onComplete]);

  // Cancel dialog
  const cancelDialog = useCallback(() => {
    onCancel();
  }, [onCancel]);

  // Memoize state object
  const state = useMemo(
    () => ({
      currentStep,
      formData,
      isValid,
      isSubmitting,
      errors,
    }),
    [currentStep, formData, isValid, isSubmitting, errors]
  );

  // Memoize actions to prevent unnecessary re-renders
  const actions: DialogActions = useMemo(
    () => ({
      updateFormData,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      submitDialog,
      cancelDialog,
    }),
    [updateFormData, goToStep, goToNextStep, goToPreviousStep, submitDialog, cancelDialog]
  );

  // Memoize context value
  const contextValue: DialogContextValue = useMemo(
    () => ({
      state,
      actions,
      isEditMode: Boolean(initialPolicy),
    }),
    [state, actions, initialPolicy]
  );

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
}
